import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiHttpError } from '@/api/client';
import { getDuels, createDuel, type CreateDuelRequest } from '@/api/duels';
import { trackUmamiEvent } from '@/helpers/analytics';
import { notify } from '@/services/notifications';
import type { DuelDto } from '@/types/duels';

export interface UiDuelPlayer {
  name: string;
  team: {
    id: string;
    name: string;
    short_name: string;
    code: string;
  };
  position: string;
  photo?: string;
  nowCost?: number;
  totalPoints?: number;
  form?: string;
  selectedByPercent?: string;
  availability_pct?: number | null;
  availability_status?: string | null;
  availability_news?: string | null;
  solioProjectedPoints?: number | null;
  gameweek_stats?: {
    total_points?: number;
    is_final?: boolean;
    minutes?: number;
    fixtures?: Array<{
      fixture_fpl_id?: number;
      finished_provisional?: boolean | null;
      finished?: boolean | null;
      started?: boolean | null;
      team_h_score?: number | null;
      team_a_score?: number | null;
      kickoff_time?: string | null;
      // DGW fields: per-fixture player stats
      player_points?: number | null;
      player_minutes?: number | null;
      player_is_final?: boolean | null;
      team_h?: {
        id: string;
        fpl_id: number;
        name: string;
        short_name: string;
        code?: string;
      } | null;
      team_a?: {
        id: string;
        fpl_id: number;
        name: string;
        short_name: string;
        code?: string;
      } | null;
    }>;
  };
}

export interface UiDuel {
  id: string;
  slug: string;
  deadlineTime: string;
  gameweekId?: string;
  player1: UiDuelPlayer;
  player2: UiDuelPlayer;
  votes: {
    total_votes: number;
    player1_votes: number;
    player2_votes: number;
    pick_change_count?: number;
    last_change_at?: string | null;
    had_early_bonus_on_current_pick?: boolean;
    prediction_outcome?: 'correct' | 'incorrect' | 'tie' | 'voided' | 'no_vote' | null;
    rating_change?: number | null;
  };
  potential_rating?: {
    if_player_a_wins: number;
    if_player_a_loses: number;
    if_player_a_tie: number;
    if_player_b_wins: number;
    if_player_b_loses: number;
    if_player_b_tie: number;
    selected_player_id: string | null;
    kPop: number;
    kBase: number;
    kEarly: number;
    rating_multiplier?: number;
  } | null;
  state: 'active' | 'locked' | 'resolved' | 'partial' | 'cancelled';
  selectedPlayerId: 'player1' | 'player2' | null;
  playerAId?: string; // UUID
  playerBId?: string; // UUID
  // Duel of the Week fields
  isDuelOfTheWeek: boolean;
  ratingMultiplier: number;
  // Rating impact factors (camelCase from API snake_case)
  ratingFactors?: {
    duelWeight: string;
    duelWeightDetail: string;
    earlyBonusValue: string;
    earlyBonusDetail: string;
    isDuelOfTheWeek?: boolean;
    ratingFactorsDebug?: {
      N: number;
      Nmin: number;
      Nmax: number;
      kPop_formula: string;
      hours_before_deadline?: number;
      kEarly_bracket?: string;
    };
    humanVotesPlayerA?: number;
    humanVotesPlayerB?: number;
  } | null;
  createdAt?: string; // ISO 8601, from API created_at
}

const pos = (t?: number) => ({ 1: 'GK', 2: 'DEF', 3: 'MID', 4: 'FWD' }[t || 0] || 'N/A');

function mapToUi(duel: DuelDto): UiDuel {
  const pA = duel.player_a;
  const pB = duel.player_b;
  const votes = duel.votes;
  const state: UiDuel['state'] =
    duel.status === 'resolved' ? 'resolved'
      : duel.status === 'locked' ? 'locked'
        : duel.status === 'partial' ? 'partial'
          : duel.status === 'cancelled' ? 'cancelled'
            : 'active';
  const selectedPlayerId: UiDuel['selectedPlayerId'] = votes?.user_vote
    ? votes.user_vote === pA?.id
      ? 'player1'
      : votes.user_vote === pB?.id
        ? 'player2'
        : null
    : null;
  return {
    id: duel.id,
    slug: duel.slug,
    deadlineTime: duel.deadline_time,
    gameweekId: duel.gameweek_id ?? undefined,
    player1: {
      name: pA?.web_name || 'Player A',
      team: pA?.team ? {
        id: pA.team.id,
        name: pA.team.name,
        short_name: pA.team.short_name,
        code: pA.team.code ? String(pA.team.code) : '',
      } : {
        id: '',
        name: '—',
        short_name: '—',
        code: '',
      },
      position: pos(pA?.element_type),
      photo: pA?.photo ?? undefined,
      nowCost: pA?.now_cost,
      totalPoints: pA?.total_points,
      form: pA?.form,
      selectedByPercent: pA?.selected_by_percent,
      availability_pct: pA?.availability_pct ?? undefined,
      availability_status: pA?.availability_status ?? undefined,
      availability_news: pA?.availability_news ?? undefined,
      solioProjectedPoints: pA?.solio_projected_points ?? undefined,
      gameweek_stats: (pA?.gameweek_stats && (pA.gameweek_stats.total_points !== undefined || pA.gameweek_stats.minutes !== undefined || pA.gameweek_stats.is_final !== undefined || (pA.gameweek_stats.fixtures && pA.gameweek_stats.fixtures.length > 0))) ? {
        total_points: pA.gameweek_stats.total_points ?? undefined,
        is_final: pA.gameweek_stats.is_final ?? undefined,
        minutes: pA.gameweek_stats.minutes ?? undefined,
        fixtures: pA.gameweek_stats.fixtures ?? undefined,
      } : undefined,
    },
    player2: {
      name: pB?.web_name || 'Player B',
      team: pB?.team ? {
        id: pB.team.id,
        name: pB.team.name,
        short_name: pB.team.short_name,
        code: pB.team.code ? String(pB.team.code) : '',
      } : {
        id: '',
        name: '—',
        short_name: '—',
        code: '',
      },
      position: pos(pB?.element_type),
      photo: pB?.photo ?? undefined,
      nowCost: pB?.now_cost,
      totalPoints: pB?.total_points,
      form: pB?.form,
      selectedByPercent: pB?.selected_by_percent,
      availability_pct: pB?.availability_pct ?? undefined,
      availability_status: pB?.availability_status ?? undefined,
      availability_news: pB?.availability_news ?? undefined,
      solioProjectedPoints: pB?.solio_projected_points ?? undefined,
      gameweek_stats: (pB?.gameweek_stats && (pB.gameweek_stats.total_points !== undefined || pB.gameweek_stats.minutes !== undefined || pB.gameweek_stats.is_final !== undefined || (pB.gameweek_stats.fixtures && pB.gameweek_stats.fixtures.length > 0))) ? {
        total_points: pB.gameweek_stats.total_points ?? undefined,
        is_final: pB.gameweek_stats.is_final ?? undefined,
        minutes: pB.gameweek_stats.minutes ?? undefined,
        fixtures: pB.gameweek_stats.fixtures ?? undefined,
      } : undefined,
    },
    votes: {
      total_votes: votes?.total_votes || 0,
      player1_votes: votes?.player_a_votes || 0,
      player2_votes: votes?.player_b_votes || 0,
      pick_change_count: votes?.pick_change_count,
      last_change_at: votes?.last_change_at,
      had_early_bonus_on_current_pick: votes?.had_early_bonus_on_current_pick,
      prediction_outcome: votes?.prediction_outcome ?? undefined,
      rating_change: votes?.rating_change ?? undefined,
    },
    potential_rating: duel.potential_rating ?? undefined,
    state,
    selectedPlayerId,
    playerAId: pA?.id,
    playerBId: pB?.id,
    // Duel of the Week fields
    isDuelOfTheWeek: duel.is_duel_of_the_week ?? false,
    ratingMultiplier: duel.rating_multiplier ?? 1,
    // Rating factors
    ratingFactors: duel.rating_factors
      ? {
        duelWeight: duel.rating_factors.duel_weight,
        duelWeightDetail: duel.rating_factors.duel_weight_detail,
        earlyBonusValue: duel.rating_factors.early_bonus_value,
        earlyBonusDetail: duel.rating_factors.early_bonus_detail,
        isDuelOfTheWeek: duel.rating_factors.is_duel_of_the_week,
        ratingFactorsDebug: duel.rating_factors.rating_factors_debug,
        humanVotesPlayerA: votes?.player_a_votes ?? undefined,
        humanVotesPlayerB: votes?.player_b_votes ?? undefined,
      }
      : undefined,
    createdAt: duel.created_at,
  };
}

const DUELS_PAGE_SIZE = 50;

export interface UseDuelsParams {
  gameweekId?: string;
  featuredCollectionSlug?: string;
  playerIds?: string[]; // массив UUID игроков (1 или 2)
  position?: number; // 1, 2, 3, 4
  teamId?: string; // UUID команды
  myVote?: 'voted' | 'unvoted'; // фильтр по голосам пользователя
  myDuels?: boolean; // дуэли, созданные текущим пользователем
  createdAfter?: string; // ISO 8601
  fplTeam?: boolean;
  priceMin?: number;
  priceMax?: number;
  /** When false, duels query does not run (e.g. my_duels without session) */
  enabled?: boolean;
}

export function useDuels(params: UseDuelsParams = {}) {
  const {
    gameweekId,
    featuredCollectionSlug,
    playerIds,
    position,
    teamId,
    myVote,
    myDuels,
    createdAfter,
    fplTeam,
    priceMin,
    priceMax,
    enabled = true,
  } = params;

  const query = useInfiniteQuery({
    queryKey: ['duels', {
      gameweekId,
      featuredCollectionSlug,
      playerIds,
      position,
      teamId,
      myVote,
      myDuels,
      createdAfter,
      fplTeam,
      priceMin,
      priceMax,
      include: true,
    }],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await getDuels({
        is_active: true,
        include_joins: true,
        order: 'total_votes.desc',
        limit: DUELS_PAGE_SIZE,
        offset: pageParam,
        ...(gameweekId ? { gameweek_id: gameweekId } : {}),
        ...(featuredCollectionSlug ? { featured_collection_slug: featuredCollectionSlug } : {}),
        ...(playerIds && playerIds.length > 0 ? { player_id: playerIds } : {}),
        ...(position !== undefined ? { position } : {}),
        ...(teamId ? { team_id: teamId } : {}),
        ...(myVote ? { my_vote: myVote } : {}),
        ...(myDuels === true ? { my_duels: true } : {}),
        ...(createdAfter ? { created_after: createdAfter } : {}),
        ...(fplTeam ? { fpl_team: true } : {}),
        ...(priceMin !== undefined ? { price_min: priceMin } : {}),
        ...(priceMax !== undefined ? { price_max: priceMax } : {}),
      });
      return {
        duels: res.duels.map(mapToUi),
        pagination: res.pagination,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const { offset, limit, total_count } = lastPage.pagination;
      const nextOffset = offset + limit;
      return nextOffset < total_count ? nextOffset : undefined;
    },
    staleTime: 10 * 1000, // Reduced stale time to get fresh gameweek stats more often
    refetchOnWindowFocus: true, // Refetch when user returns to tab - fixes votes disappearing after long inactivity
    enabled,
  });

  // Flatten all pages into a single array
  const duels = query.data?.pages.flatMap((page) => page.duels) ?? [];

  // Get total count from the first page (it's the same across all pages)
  const totalCount = query.data?.pages[0]?.pagination.total_count ?? 0;

  return {
    duels,
    totalCount,
    loading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
  };
}

// Backward compatibility for existing imports
export function useOptimizedDuels(params: UseDuelsParams = {}) {
  return useDuels(params);
}

export function useCreateDuel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateDuelRequest) => createDuel(body),
    onSuccess: (_, variables) => {
      trackUmamiEvent('duel_create_success', {
        playerAId: variables.player_a_id,
        playerBId: variables.player_b_id,
      });
      // invalidate duels lists
      queryClient.invalidateQueries({ queryKey: ['duels'] });
    },
    onError: (error) => {
      const apiError = error as ApiHttpError;
      if (apiError.status === 403 && apiError.code === 'EMAIL_NOT_VERIFIED') {
        const message = apiError.message || 'Email verification required. Please verify your email to create duels.';
        notify.warning(message);
      }
    },
  });
}
